/**
 * MultiAgentIdePanel — the dev-agent IDE, replicated VERBATIM for the multi-agent.
 *
 * Same colours, same layout, same components and behaviour as pages/dev-agent
 * (DevAgent.tsx): a persistent left sidebar (metadata explorer + rules), the
 * full MetadataEditorPanel (save / validate / compile / compare / run-test, via
 * the agentic-platform backend), and the RightPanels (dependencies + tests).
 *
 * The ONLY difference from the dev-agent is the centre feed: instead of the
 * dev-agent AgentFeed, the multi-agent ChatPanel renders the multi-step-agentic
 * run. Everything else is the exact dev-agent component, reused — not rebuilt.
 */
import { useEffect, useRef, useState } from "react";
import { useAuthStoreV1 } from "@/stores/authStore_v1";
import { ErrorBoundary } from "@/components/organisms/ErrorBoundary";
import { PanelLeftClose, PanelLeftOpen, PanelRightOpen } from "lucide-react";
import { useMultiAgentStore } from "@/stores/multiAgentStore";
import { useMultiAgentIdeStore } from "@/pages/multi-agent/ide/store/multiAgentIdeStore";
import { SidebarMetadataSection } from "@/pages/dev-agent/components/SidebarMetadataSection";
// import { SidebarRules } from "@/pages/dev-agent/components/SidebarRules";
import { useSidebarMetadata } from "@/pages/dev-agent/components/useSidebarMetadata";
import { MultiAgentMetadataEditorPanel } from "@/pages/multi-agent/ide/editor/MultiAgentMetadataEditorPanel";
import { useMetadataEditorState } from "@/pages/dev-agent/components/useMetadataEditorState";
import { useResizeDrag } from "@/pages/dev-agent/useResizeDrag";
import { DevAgentResizeGrip } from "@/pages/dev-agent/components/DevAgentResizeGrip";
import {
  selectWorkspaceSection,
  useWeaveWorkspaceStore,
} from "@/stores/weaveWorkspaceStore";
import { useMultiAgentEditorBridge } from "@/pages/multi-agent/ide/useMultiAgentEditorBridge";
import { bareClassName } from "@/pages/multi-agent/ide/utils/bareClassName";
import { layoutConfig, resolveSidebarMode, type MultiAgentLayoutVariant } from "@/pages/multi-agent/ide/layoutVariant";
import { runChrome } from "@/services/multi-agent/weaveChrome";
import { servedProtocolFor } from "@/services/multi-agent/protocol/negotiation";
import { selectRunStatus, useRunStateStore } from "@/stores/runStateStore";

export function MultiAgentIdePanel({
  variant,
  composer,
  conversation,
  workspace,
}: {
  variant?: MultiAgentLayoutVariant;
  composer?: React.ReactNode;
  /**
   * The conversation surface, ALWAYS SUPPLIED — `WeaveTranscript` when the server is serving the
   * protocol, the legacy `ChatPanel` when it is serving legacy. The route decides, because the
   * route is where the negotiated answer lives.
   *
   * A SLOT RATHER THAN A PROTOCOL READ HERE, for the same reason `composer` is one: the handlers
   * the transcript needs (`onPlanApproved`, the stream `refresh`) belong to the route, and passing
   * the built element keeps this component ignorant of the protocol. There is deliberately NO
   * fallback for an omitted `conversation`: a missing transcript is a blank page, which is the one
   * failure this whole seam exists to prevent.
   */
  conversation?: React.ReactNode;
  /**
   * The right-hand inspection surface — `WeaveWorkspace`, which REPLACED `MultiAgentRightPanels`
   * (deleted in 4a1d765f). It reads `runStateStore`, so it is blank on a legacy run; the edge then
   * falls through to the code editor. A known chrome gap, recorded in `protocol/negotiation.ts`.
   */
  workspace?: React.ReactNode;
} = {}) {
  const orgId = useAuthStoreV1((s) => s.selectedOrg?.id);

  // Layout-only variant config (which panels start collapsed, chat width, editor
  // side). It changes ARRANGEMENT only — every panel/component below is reused
  // unchanged, so chat / editor / agent behaviour is identical across variants.
  const cfg = layoutConfig(variant);

  // MA-3: runStatus is the MULTI-AGENT run status (mirrored into the IDE store),
  // never the dev-agent session.
  /*
   * ── THE BUSY SIGNAL, AND WHY IT CANNOT BE `runStatus` ALONE ───────────────
   *
   * `multiAgentIdeStore.runStatus` mirrors `multiAgentStore.status`, which the LEGACY stream writes.
   * On v1 that stream is never opened, so the field keeps the `"starting"` `startRun` wrote — and
   * `mapRunStatus` folds `starting` into `"running"`. Net effect on v1: the metadata explorer went
   * disabled on the first run and never came back, so after an answer landed the file list still
   * looked live — rows highlighting, tooltips opening — and nothing happened on click. Reported from
   * a real session; the same field also drove the editor's and the right panels' busy states.
   *
   * `runChrome` already owns this question — same mapping, same `input_required` handling as the
   * composer and the header, and the same negotiated choice of WHICH store describes the run — so
   * this reads through it rather than inventing a second rule. It was `weaveChrome`, which is the
   * v1 arm only: on a legacy run `runBusy` was permanently false and the file list stayed clickable
   * mid-run, which is the exact defect the paragraph above records, on the other path.
   * `cancellable` is irrelevant to `inFlight`, hence the `false`.
   */
  const weaveRunStatus = useRunStateStore(selectRunStatus);
  const protocolRunId = useRunStateStore((s) => s.runId);
  const legacyBusyStatus = useMultiAgentStore((s) => s.status);
  const legacyReadOnly = useMultiAgentStore((s) => s.readOnly);
  const runBusy = runChrome(
    servedProtocolFor(protocolRunId) === "1",
    weaveRunStatus,
    false,
    { readOnly: legacyReadOnly, status: legacyBusyStatus },
  ).inFlight;
  const runningTest = useMultiAgentIdeStore((s) => s.runningTest);

  // ── Layout state (variant-driven, distinct at rest) ──────────────────
  // Each side panel has three modes: full (resizable column), rail (40px icon
  // strip) or hidden (off-canvas, revealed by a floating chevron). `collapsed`
  // tracks rail-vs-full; `hidden` tracks whether it's rendered at all.
  // The left sidebar starts as a rail and is off-canvas below 1024 px (`resolveSidebarMode`); the
  // History drawer is the ONE session list, so the sidebar carries Files only.
  const initialSidebar = resolveSidebarMode(cfg.sidebar, typeof window === "undefined" ? 1280 : window.innerWidth);
  const [sidebarHidden, setSidebarHidden] = useState(initialSidebar === "hidden");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(initialSidebar !== "full");
  const narrowRef = useRef(initialSidebar === "hidden" && cfg.sidebar !== "hidden");
  useEffect(() => {
    // Crossing the 1024 px line re-applies the default once; a reader's own choice is untouched in between.
    const onResize = () => {
      const mode = resolveSidebarMode(cfg.sidebar, window.innerWidth);
      const narrow = mode === "hidden" && cfg.sidebar !== "hidden";
      if (narrow === narrowRef.current) return;
      narrowRef.current = narrow;
      setSidebarHidden(mode === "hidden");
      setSidebarCollapsed(mode !== "full");
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [cfg.sidebar]);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [rightHidden, setRightHidden] = useState(cfg.right === "hidden");
  const [rightPanelCollapsed, setRightPanelCollapsed] = useState(cfg.right !== "full");
  // MA-6: editor open/close is owned by this panel (local state), not the
  // dev-agent store.
  const [editorOpen, setEditorOpen] = useState(false);
  const sidebar = useResizeDrag({ min: 180, max: 400, defaultValue: 240 });
  /*
   * `reverse` FOLLOWS THE EDGE THE PANEL SITS ON, and on v1 the editor changed edges.
   *
   * The grip drags a width from pointer movement, so its sign depends on which side of the panel it
   * is. Legacy docks the editor LEFT of the chat with the grip on its right (`reverse: false`); v1
   * puts it on the RIGHT with the grip on its left, exactly like `rightPanel` below — so without
   * this the drag would have felt inverted: pull left, panel shrinks.
   */
  /*
   * The editor docks to the RIGHT, always. This was `workspace != null` — the signal that the route
   * was on the protocol path — and that path is now the only one, so the left-docked legacy
   * arrangement is gone with it.
   */
  const editorOnRightEdge = true;
  const editorPanel = useResizeDrag({
    min: 350,
    max: 900,
    defaultValue: 500,
    reverse: editorOnRightEdge,
  });
  const rightPanel = useResizeDrag({ min: 200, max: 500, defaultValue: cfg.rightWidth, reverse: true });

  // ── Sidebar metadata (reused, org-scoped) ───────────────────────────
  const [selectedMetadata, setSelectedMetadata] = useState<{ id: string; name: string } | null>({ id: "ApexClass", name: "ApexClass" });
  const [isQuickOpenOpen, setIsQuickOpenOpen] = useState(false);
  const { metadataTypeOptions, metadataFiles, loadingFiles, loadingMoreFiles, metadataSearchTerm, hasNextPage, handleLoadMore } = useSidebarMetadata(selectedMetadata);

  // ── Editor state (reused tab/state hook — no dev-agent session) ──────
  // setPendingFile is a no-op here: the multi-agent refactor target is owned by
  // useMultiAgentIdeStore.pendingRefactorFile, not a dev-agent pending file.
  const editor = useMetadataEditorState(() => {});
  const {
    analyzingFile,
    mergedInitialContent,
    handleOpenFileInEditor,
    handleCloseEditor,
    handleLoadIntoChat,
  } = useMultiAgentEditorBridge({
    editor,
    editorOpen,
    setEditorOpen,
    setSidebarCollapsed,
  });

  // MA-14: track the active editor file's bare class name in the IDE store so
  // the coverage consumers (runTest + test SSE) can light the Monaco gutter for
  // the file the user is looking at.
  const activeFileName = editor.openFiles.find((f) => f.id === editor.activeFileId)?.name;
  useEffect(() => {
    useMultiAgentIdeStore.getState().setActiveEditorClassName(bareClassName(activeFileName));
  }, [activeFileName]);

  // New session / session switch (nonce bumped by the New-session + session
  // open/delete actions): close the editor's open files — they belong to the
  // PRIOR session, and leaving them open makes the metadata query keep firing at
  // a dead id, 404-ing ("Metadata item not found") during the transition.
  const newSessionNonce = useMultiAgentIdeStore((s) => s.newSessionNonce);
  const closeEditorRef = useRef(editor.handleCloseMetadataEditor);
  closeEditorRef.current = editor.handleCloseMetadataEditor;
  const firstNonceRef = useRef(true);
  useEffect(() => {
    if (firstNonceRef.current) { firstNonceRef.current = false; return; }
    closeEditorRef.current();
  }, [newSessionNonce]);

  /** Sidebar file open: preview on a single click, permanent on a double click. */
  const openSidebarFile = (
    file: { id: string | number; name: string; metadataType?: string; content?: string },
    { preview }: { preview: boolean },
  ) => {
    useMultiAgentIdeStore.getState().setPendingRefactorFile({
      id: String(file.id),
      name: file.name,
      content: file.content || "",
      metadataType: file.metadataType,
    });
    /*
     * v1: opening a file takes the right edge, so any section the reader had open there is closed.
     * Without this the editor would mount behind an open Changes board and the click would look
     * like it did nothing. Legacy is unaffected — it has no workspace store in play.
     */
    useWeaveWorkspaceStore.getState().close();
    handleOpenFileInEditor(
      { id: String(file.id), name: file.name, metadataType: file.metadataType },
      { preview },
    );
  };


  // MA-15: when the agent produces a proposed artifact derived from a source
  // (`artifact_created.metadata.source_artifact_id`), open it as a tab showing
  // the refactored code and stash the original so the user can review it — but
  // do NOT auto-open the diff. The diff opens ONLY when the user clicks Compare
  // in the editor toolbar (per product decision: never auto-compare).
  // MA-17: badge "C" (created/new) when there is no source artifact id.
  const artifacts = useMultiAgentStore((s) => s.artifacts);
  const sourceArtifactIds = useMultiAgentIdeStore((s) => s.sourceArtifactIds);
  const handledCompareArtifactsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    // Find the newest artifact carrying a source link that we haven't opened yet.
    const candidate = [...artifacts]
      .reverse()
      .find((a) => sourceArtifactIds[a.id] && !handledCompareArtifactsRef.current.has(a.id));
    if (!candidate) return;
    handledCompareArtifactsRef.current.add(candidate.id);
    void (async () => {
      try {
        const { getArtifact } = await import("@/services/multi-agent");
        const art = await getArtifact(candidate.id);
        let proposed = art.content || "";
        if (!proposed) return;
        // Refactor *diff artifacts* carry their before/after INSIDE the content
        // as JSON ({fixes:[{source:{original,replacement,unified_diff}}]}), and
        // their source_artifact_id points at the blast-radius JSON — NOT the
        // original class. Parse it so Compare shows real apex (original vs
        // replacement) instead of raw JSON diffed against a grep result.
        let diffOriginal: string | null = null;
        let isDeleteFix = false;
        if (proposed.trimStart().startsWith("{")) {
          try {
            const parsed = JSON.parse(proposed) as {
              fixes?: Array<{
                kind?: string;
                source?: { original?: string; replacement?: string };
              }>;
            };
            // A delete fix has replacement === "" BY DESIGN (no new body) — it
            // must still match here, or it falls through as "not a diff-json
            // artifact" and the raw JSON envelope gets shown as if it were code.
            const fix = parsed.fixes?.find(
              (f) => f?.source?.replacement || f?.kind === "delete",
            );
            if (fix) {
              isDeleteFix = fix.kind === "delete";
              diffOriginal = fix.source?.original ?? "";
              // The tab's baseline content: a delete has no new body to show as
              // "the file", so show the ORIGINAL (matches codeActions.ts).
              proposed = isDeleteFix ? diffOriginal : (fix.source?.replacement ?? proposed);
            }
          } catch {
            /* not a diff-json artifact — keep the raw content */
          }
        }
        const metadataType =
          (art.metadata?.metadata_type as string) ||
          (art.language === "trigger" ? "ApexTrigger" : "ApexClass");
        // Open the proposed artifact as a tab showing the refactored code. We
        // do NOT call editor.setAcceptedCode here — that would auto-open the
        // diff. Instead we stash the target + original so the Compare button can
        // open the diff on demand.
        editor.openTab({ id: candidate.id, name: candidate.name, metadataType });
        editor.setInitialContent((prev) => ({ ...prev, [candidate.id]: proposed }));
        setEditorOpen(true);
        const ide = useMultiAgentIdeStore.getState();
        // Record which tab the diff belongs to and the in-hand original (left
        // side) so the manual Compare button shows refactored-vs-original.
        // Deliberately NOT arming acceptedCodeOperation="deleted" here — MA-15
        // fires passively on ANY artifact arrival, before the user has looked
        // at anything. Arming delete-intent from a passive effect means a
        // reflexive Save (no review at all) could delete a file the user never
        // saw. Only the explicit "Review Delete" card action (codeActions.ts)
        // may arm it; this effect just makes sure the tab shows real Apex
        // (the original body) instead of the raw JSON envelope.
        ide.setAcceptedCodeTarget(candidate.id);
        ide.setAcceptedCodeOriginal(diffOriginal);
        // Always set explicitly (never skip) — acceptedCodeTarget just moved to
        // THIS tab, so a stale "deleted" from a prior tab's unresolved review
        // must not silently carry over and arm isDeleteTarget for this one.
        ide.setAcceptedCodeOperation(isDeleteFix ? null : diffOriginal != null ? "modified" : null);
        // MA-17: "C" when there is no original (new file); otherwise it's a diff.
        ide.setArtifactBadge(
          candidate.id,
          diffOriginal != null || sourceArtifactIds[candidate.id] ? "M" : "C",
        );
      } catch (err) {
        console.warn("[multi-agent compare] auto-open failed", err);
      }
    })();
  }, [artifacts, sourceArtifactIds, editor, setEditorOpen]);

  // ── Panel blocks (built once, then ARRANGED per layout variant) ──────
  // Each block is the exact same component/markup as the original single
  // layout; only their order, default-collapsed state, sizing and the chat
  // width differ between variants. Nothing functional changes.
  const sidebarBlock = sidebarHidden ? null : !sidebarCollapsed ? (
    <div
      className="ma-left-utility min-w-[180px] max-w-[400px] flex flex-col overflow-hidden shrink-0 min-h-0 h-full"
      style={{ width: sidebar.width }}
    >
      <div className="ma-panel-toolbar flex shrink-0 items-center gap-1 px-2 py-1 z-10">
        <span className="px-2 py-1 text-xs font-medium text-foreground">
          Files{metadataFiles.length ? ` · ${metadataFiles.length}` : ""}
        </span>
        <button onClick={() => setSidebarCollapsed(true)} className="ml-auto p-1 rounded hover:bg-muted/50 transition-colors" title="Collapse sidebar">
          <PanelLeftClose className="w-3.5 h-3.5 text-muted-foreground" />
        </button>
      </div>
      <div className="ma-panel-section ma-panel-section--grow flex-1 min-h-0 overflow-y-auto text-sm">
          <SidebarMetadataSection
            metadataTypeOptions={metadataTypeOptions} selectedMetadata={selectedMetadata} setSelectedMetadata={setSelectedMetadata}
            metadataFiles={metadataFiles} loadingFiles={loadingFiles} loadingMoreFiles={loadingMoreFiles}
            metadataSearchTerm={metadataSearchTerm} hasNextPage={hasNextPage} isQuickOpenOpen={isQuickOpenOpen}
            setIsQuickOpenOpen={setIsQuickOpenOpen} onLoadMore={handleLoadMore}
            // Single click previews (one tab, reused); double click keeps the tab.
            // Both mark the class as the refactor target: its UUID + name + source
            // is seeded into the next multi-agent run so the agent refactors that
            // exact class, and that is true however the file was opened.
            onOpenFileInEditor={(file) => openSidebarFile(file, { preview: true })}
            onOpenFilePersistent={(file) => openSidebarFile(file, { preview: false })}
            // Mirrors the editor: the row for the active tab is highlighted, so the
            // sidebar and the tab strip never disagree about which file you are on.
            activeFileId={editor.activeFileId}
            disabled={runBusy}
          />
      </div>
      {/* TODO: Removed the rules from the multiAgent for now */}
      {/* Rules — stacked below the files, COLLAPSED by default so Files/Sessions
          get the full column height; expands on demand, never eats the panel. */}
      {/* <div className="ma-panel-section shrink-0 border-t border-border/40">
        <button
          type="button"
          onClick={() => setRulesOpen((v) => !v)}
          className="flex w-full items-center gap-2 px-3 py-2 text-xs font-semibold text-foreground transition-colors hover:bg-muted/30"
        >
          {rulesOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          Rules
        </button>
        {rulesOpen && (
          <div className="max-h-[30vh] overflow-y-auto pb-2 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-muted-foreground/40 [&::-webkit-scrollbar-thumb]:rounded-full">
            <SidebarRules userId={useAuthStoreV1.getState().user?.id || ""} />
          </div>
        )}
      </div> */}
    </div>
  ) : (
    <div className="w-10 border-r border-border/50 flex flex-col items-center pt-2 shrink-0">
      <button onClick={() => setSidebarCollapsed(false)} className="p-1.5 rounded hover:bg-muted/50 transition-colors" title="Expand sidebar">
        <PanelLeftOpen className="w-4 h-4 text-muted-foreground" />
      </button>
    </div>
  );

  const sidebarGrip = !sidebarHidden && !sidebarCollapsed ? (
    <DevAgentResizeGrip onMouseDown={sidebar.onMouseDown} title="Drag to resize sidebar" />
  ) : null;

  // Code Editor — the real dev-agent editor (save / compare / run-test).
  const editorEl = editorOpen && editor.openFiles.length > 0 ? (
    <ErrorBoundary fallback={<div className="flex items-center justify-center h-full text-sm text-muted-foreground">Editor crashed — close and reopen the file</div>}>
      <MultiAgentMetadataEditorPanel
        openFiles={editor.openFiles} activeFileId={editor.activeFileId}
        // Activation only — routing this through openTab would silently promote a
        // preview tab just because the user looked at it.
        onActiveFileChange={(id) => id && editor.activateTab(id)}
        onCloseTab={editor.handleCloseTab} onReorderTabs={editor.reorderTabs}
        onPromoteTab={editor.promoteTab} panelWidth={editorPanel.width}
        onClose={handleCloseEditor} acceptedCode={editor.acceptedCode}
        onAcceptedCodeChange={(code) => editor.setAcceptedCode(code)} onPendingFileChange={() => {}}
        initialContent={mergedInitialContent} orgId={orgId} onLoadIntoChat={handleLoadIntoChat}
        isAnalyzing={!!analyzingFile}
        onCanonicalFileId={(canonicalId, previousId) => { editor.migrateFileId(previousId, canonicalId); }}
        onFileSaved={async (data) => {
          editor.setInitialContent((prev) => {
            const next = { ...prev };
            delete next[data.id];
            return next;
          });
        }}
        /*
         * NO `extraActions`, and "Add to Workspace" is gone with it.
         *
         * It wrote the open file into `multiAgentStore.artifacts` so it would appear in
         * `MultiAgentWorkspacePanel` — a permanent, manually curated pin list. Two reasons it went:
         *
         *   · On the v1 path it was already DEAD. That panel lives inside
         *     `MultiAgentRightPanels`, which v1 does not mount (see the `rightBlock` note below),
         *     so the click wrote to a surface nobody could see.
         *   · The product direction is that workspace entry is CONTEXTUAL — the user interacts with
         *     a meaningful result and the relevant surface opens. A manual "pin this for later"
         *     step is the opposite of that, and deliberately has no replacement.
         *
         * Legacy loses manual pinning. That was an explicit product decision, not an oversight.
         */
        isBusy={runBusy}
        isTestRunning={!!runningTest}
      />
    </ErrorBoundary>
  ) : null;

  /*
   * ── ONE CONTEXTUAL SURFACE ON THE RIGHT (v1) ──────────────────────────────
   *
   * `workspace != null` IS the v1 signal — the route only passes that slot when the flag is on, and
   * `rightBlock` below has always branched on it.
   *
   * WHAT WAS WRONG. The editor docks to the LEFT of the chat on this variant, so opening a file from
   * the sidebar inserted a third column *inside* the centre and squeezed the conversation, while a
   * code block or an artifact clicked IN the conversation opened on the RIGHT. The same kind of
   * content appeared on either side depending on how you got to it, and a file click took space from
   * the chat on the wrong edge.
   *
   * Now the right edge is the single place detail appears, whatever opened it, and the chat owns the
   * centre outright. `weaveWorkspaceStore.section` decides which of the two is showing: a section the
   * reader opened from the conversation wins, otherwise the editor if a file is open, otherwise
   * neither and the conversation takes the whole width.
   */
  const workspaceSection = useWeaveWorkspaceStore(selectWorkspaceSection);
  const contextual = editorOnRightEdge;

  const editorGrip = editorEl ? (
    <DevAgentResizeGrip onMouseDown={editorPanel.onMouseDown} title="Drag to resize editor" />
  ) : null;

  // Centre feed — the MULTI-AGENT chat. `--ma-prose-max` controls the reading
  // width: "100%" lets prose fill the column; a fixed value (Focus variant)
  // centers it into a comfortable reading measure.
  const proseMax =
    cfg.chat === "narrow" ? "720px" : cfg.chat === "wide" ? "960px" : "100%";
  const chatBlock = (
    <div
      className="ma-center-workspace relative z-[2] flex flex-col min-h-0 h-full min-w-0 flex-1"
      style={{ ["--ma-prose-max" as string]: proseMax } as React.CSSProperties}
    >
      <div className="flex min-w-0 min-h-0 flex-1">
        {/*
          The transcript, always. `conversation` used to fall back to the legacy `ChatPanel` when the
          route did not supply it; the route always supplies it now, so that fallback was dead — and
          with it the last thing that mounted `ChatPanel`.
        */}
        {conversation}
      </div>
      {composer != null && <div className="shrink-0">{composer}</div>}
    </div>
  );

  // Rightmost — ONE consolidated panel (Run details + Dependencies + Tests)
  // reading the MULTI-AGENT session via useMultiAgentIdeStore.
  const rightGrip = !rightHidden && !rightPanelCollapsed ? (
    <DevAgentResizeGrip onMouseDown={rightPanel.onMouseDown} title="Drag to resize right panel" />
  ) : null;
  /*
   * ── ONE RIGHT-HAND SURFACE NOW, AND WHAT THAT COSTS THE LEGACY PATH. ────────────────────────
   *
   * There used to be two: `MultiAgentRightPanels` (Run details · Workspace · Impact · Tests ·
   * Dependencies — a persistent column, fed by `multiAgentStore`/`multiAgentIdeStore` from the
   * legacy SSE) and `WeaveWorkspace`. The former was deleted in 4a1d765f when the legacy branch
   * was believed unreachable. It is not unreachable any more — the server can serve legacy — so
   * a legacy run now gets the editor or nothing on this edge. Recorded as a known gap rather than
   * half-restored; the conversation, the answer and the clarification card all render.
   *
   * The two models disagreed anyway, and that was the reason for replacing rather than extending:
   *
   *   · Those four panels are fed by the legacy stream, and `selectRunStreams` does not open it
   *     when the flag is on — so on v1 they render an empty column beside the conversation.
   *   · They are a PERSISTENT dashboard: always present, several sections at once, populated by the
   *     user pinning things into them. `WeaveWorkspace` is CONTEXTUAL: closed by default, one
   *     section at a time, opened by the user interacting with a result in the conversation. The
   *     new experience is not built on top of the old column because the two models disagree about
   *     who decides what is on screen.
   *
   * The workspace is also not gated on `rightHidden`/`rightPanelCollapsed`: it owns its own
   * open/closed state, so a layout variant that starts the legacy column collapsed must not
   * suppress a section the user just opened from a card.
   */
  /*
   * ONE CONTEXTUAL SURFACE ON THE RIGHT, and now it is the only arrangement.
   *
   * `contextual ? … : <MultiAgentRightPanels …>` used to choose between this and the legacy
   * four-panel column. The legacy arm is unreachable since `/weave` became the protocol surface, and
   * removing it takes the last mount of `MultiAgentRightPanels` with it.
   *
   * Precedence is the reader's own order of intent: a section they just opened from the conversation
   * is the most recent thing they asked for, so it shows; otherwise a file they have open in the
   * editor; otherwise nothing at all, and the conversation takes the full width.
   *
   * Opening a file CLOSES any open section (see the sidebar handler above), so these never compete
   * for the edge — one surface, one occupant, always the last thing the reader chose.
   */
  const rightBlock = workspaceSection ? workspace : editorEl;

  // Split layouts keep the editor pane ALWAYS visible to the RIGHT of the chat
  // (a placeholder until a file is opened) so the two-column split reads as the
  // layout. On-demand layouts dock the editor to the LEFT of the chat, and only
  // once a file is actually open (classic behaviour).
  const splitPlaceholder =
    cfg.editor === "split" && !editorEl ? (
      <div className="flex min-w-0 flex-1 flex-col items-center justify-center gap-2 border-l border-border/50 px-6 text-center text-sm text-muted-foreground">
        <span className="font-medium text-foreground">Code editor</span>
        <span>Open a class from the sidebar to edit &amp; compare it here.</span>
      </div>
    ) : null;

  const centre = contextual ? (
    /*
     * v1: THE CENTRE IS THE CONVERSATION, full stop. The editor moved to the right edge (see
     * `rightBlock`), so nothing is inserted beside the chat and closing the detail surface hands
     * the width straight back to it.
     */
    chatBlock
  ) : cfg.editor === "split" ? (
    <>
      {chatBlock}
      {editorGrip}
      {editorEl}
      {splitPlaceholder}
    </>
  ) : (
    // Legacy on-demand: editor docks to the LEFT of the chat (next to the meta panel).
    <>
      {editorEl}
      {editorGrip}
      {chatBlock}
    </>
  );

  // Floating reveal chevrons for off-canvas (hidden) panels — the chrome-free
  // layouts (focus / zen / split) can still bring a panel back on demand.
  const sidebarReveal = sidebarHidden ? (
    <button
      type="button"
      onClick={() => { setSidebarHidden(false); setSidebarCollapsed(true); }}
      title="Show sidebar"
      className="absolute left-1 top-1 z-20 inline-flex h-7 w-7 items-center justify-center rounded-md border border-border bg-card/90 text-muted-foreground shadow-sm transition-colors hover:text-foreground"
    >
      <PanelLeftOpen className="h-4 w-4" />
    </button>
  ) : null;
  const rightReveal = rightHidden ? (
    <button
      type="button"
      onClick={() => { setRightHidden(false); setRightPanelCollapsed(true); }}
      title="Show run details"
      className="absolute right-1 top-1 z-20 inline-flex h-7 w-7 items-center justify-center rounded-md border border-border bg-card/90 text-muted-foreground shadow-sm transition-colors hover:text-foreground"
    >
      <PanelRightOpen className="h-4 w-4" />
    </button>
  ) : null;

  return (
    <div className="ma-ide-shell relative flex flex-1 min-h-0 h-full w-full overflow-hidden">
      <div className="ma-ide-body relative flex min-h-0 flex-1 overflow-hidden">
        {sidebarReveal}
        {sidebarBlock}
        {sidebarGrip}
        {centre}
        {/*
          THE GRIP BELONGS TO WHOEVER HOLDS THE EDGE.
          
          On v1 the workspace sets its own width (`clamp(360px, 34vw, 560px)`), so `rightGrip` — which
          drags `rightPanel.width` — moved nothing at all: a handle that responds to nothing is worse
          than no handle. The editor DOES take a width, so when it is the occupant its own grip
          renders instead, and when the edge is empty there is nothing to drag.
        */}
        {contextual ? (workspaceSection ? null : editorGrip) : rightGrip}
        {rightBlock}
        {!contextual && rightReveal}
      </div>
    </div>
  );
}
